import React from 'react'
import { RichTextMicroCopy } from 'pages/news/components/newsDetail/components/rich-text/RichText'
import UserContextModel from 'models/UserContextModel'
import { getPlainTextFromRichText } from '../../../../utils'
import { PlexForGoodTranslations } from '../../../../translations'
import { DonationCampaign } from '../../types'
import { GlobalErrorMessage } from '../../../validation-message'
import { ValidationErrors } from '../../donation-service'
import {
    CharacterSelect,
    PlexAmountInput,
    CampaignSelector,
    DonateButton,
} from '..'
import style from './DonationFormContent.module.scss'

interface DonationFormContentProps {
    translations: PlexForGoodTranslations
    selectedCampaign: DonationCampaign
    setSelectedCampaign: (campaign: DonationCampaign) => void
    campaigns: DonationCampaign[]
    donationAmount: number
    setDonationAmount: React.Dispatch<React.SetStateAction<number>>
    plexBalance: UserContextModel['plexBalance']
    selectedCharacterId?: number
    setSelectedCharacterId: (id?: number) => void
    characters: Array<{ id: number; name: string }>
    loading: boolean
    fetchingCharacters: boolean
    handleDonate: () => void
    errorMessage: string | null
    errorCode: string | null
    fieldErrors: ValidationErrors
    getTranslatedErrorMessage?: (code: string | null) => string | null
}

/**
 * Form content for the donation form
 * Extracted to make the main component more testable and maintainable
 */
const DonationFormContent: React.FC<DonationFormContentProps> = ({
    translations,
    selectedCampaign,
    setSelectedCampaign,
    campaigns,
    donationAmount,
    setDonationAmount,
    plexBalance,
    selectedCharacterId,
    setSelectedCharacterId,
    characters,
    loading,
    fetchingCharacters,
    handleDonate,
    errorMessage,
    errorCode,
    fieldErrors,
    getTranslatedErrorMessage,
}) => {
    const inputPlaceholder = getPlainTextFromRichText(
        translations.enterAmountOf
    )

    const zeroPlexMessage = getPlainTextFromRichText(
        translations.zeroPlexMessage
    )

    return (
        <div className={style.formContent} data-testid="donation-form-content">
            <div className={style.title}>{selectedCampaign.title}</div>
            <div className={style.formDescription}>
                <RichTextMicroCopy
                    document={translations.donationFormDescription}
                />
            </div>

            {/* Only show global error message for non-field-specific errors */}
            {(errorMessage || errorCode) &&
                Object.keys(fieldErrors).length === 0 && (
                    <GlobalErrorMessage
                        message={
                            getTranslatedErrorMessage?.(errorCode) ||
                            errorMessage
                        }
                    />
                )}
            <CampaignSelector
                campaigns={campaigns}
                selectedCampaign={selectedCampaign}
                setSelectedCampaign={setSelectedCampaign}
            />

            <form className={style.formBody}>
                <PlexAmountInput
                    plexAmount={donationAmount}
                    setPlexAmount={setDonationAmount}
                    plexBalance={plexBalance}
                    placeholder={inputPlaceholder}
                    zeroPlexMessage={zeroPlexMessage}
                    hasError={!!fieldErrors.plexAmount}
                    errorMessage={fieldErrors.plexAmount}
                    balanceText={translations.plexBalance}
                    needMorePlexText={translations.needMorePlex}
                    insufficientPlexText={translations.errorInsufficientPlex}
                />

                <CharacterSelect
                    selectedCharacterId={selectedCharacterId}
                    setSelectedCharacterId={setSelectedCharacterId}
                    characters={characters}
                    fetchingCharacters={fetchingCharacters}
                    placeholder={translations.selectCharacterPlaceHolder}
                    hasError={!!fieldErrors.characterId}
                    errorMessage={fieldErrors.characterId}
                    mustHaveCharacterMessage={translations.mustHaveCharacter}
                />

                <DonateButton
                    onClick={handleDonate}
                    isDisabled={
                        !donationAmount ||
                        characters.length === 0 ||
                        plexBalance === 'N/A'
                    }
                    loading={loading}
                    buttonText={translations.donatePlex}
                />
            </form>
        </div>
    )
}

export default DonationFormContent
